// Path shapes for ribbons: great circles on the network surface, the lifted missile flight arc, and straight
// chords. Samples go into a caller-owned Float32Array of RIBBON_POINTS xyz triples; nothing allocates.

import { Vector3 } from 'three'
import { flightLift } from '../render/geo/strikeArc'
import { RIBBON_POINTS, RIBBON_SEGMENTS } from './RibbonPool'
import type { PathShape } from './effects'

/** Paths with less angular length than this have no direction to draw. */
export const MIN_PATH_ANGLE = 1e-5
const MIN_TANGENT_LENGTH_SQ = 1e-10
const XYZ = 3

const scratchA = new Vector3()
const scratchB = new Vector3()

/** Angle between two unit vectors, clamped for rounding. */
export function angleBetween(a: Vector3, b: Vector3): number {
  return Math.acos(Math.min(1, Math.max(-1, a.dot(b))))
}

/** A path from unit normal `from` at radius `fromRadius` to `to` at `toRadius`; `angle` = angleBetween(from, to). */
export interface PathEnds {
  readonly from: Vector3
  readonly fromRadius: number
  readonly to: Vector3
  readonly toRadius: number
  readonly angle: number
}

interface PathGeometry {
  /** Point at fraction s. */
  point(ends: PathEnds, s: number, out: Vector3): Vector3
  /** Approximate world length (dash placement only). */
  length(ends: PathEnds): number
}

/** Great-circle interpolation of the unit normals (angle > MIN_PATH_ANGLE), at radius `radius`. */
function slerp(ends: PathEnds, s: number, radius: number, out: Vector3): Vector3 {
  const sinAngle = Math.sin(ends.angle)
  const wFrom = Math.sin((1 - s) * ends.angle) / sinAngle
  const wTo = Math.sin(s * ends.angle) / sinAngle
  return out.copy(ends.from).multiplyScalar(wFrom).addScaledVector(ends.to, wTo).multiplyScalar(radius)
}

function radiusAt(ends: PathEnds, s: number): number {
  return ends.fromRadius + (ends.toRadius - ends.fromRadius) * s
}

function greatCircleLength(ends: PathEnds): number {
  return (ends.angle * (ends.fromRadius + ends.toRadius)) / 2
}

const PATH_GEOMETRY: Readonly<Record<PathShape, PathGeometry>> = {
  surface: {
    point: (ends, s, out) => slerp(ends, s, radiusAt(ends, s), out),
    length: greatCircleLength,
  },
  arc: {
    point: (ends, s, out) => slerp(ends, s, radiusAt(ends, s) + flightLift(ends.angle, s), out),
    length: greatCircleLength,
  },
  straight: {
    point: (ends, s, out) => {
      scratchB.copy(ends.to).multiplyScalar(ends.toRadius)
      return out.copy(ends.from).multiplyScalar(ends.fromRadius).lerp(scratchB, s)
    },
    length: (ends) => {
      scratchA.copy(ends.from).multiplyScalar(ends.fromRadius)
      scratchB.copy(ends.to).multiplyScalar(ends.toRadius)
      return scratchA.distanceTo(scratchB)
    },
  },
}

/** Samples the path between fractions s0 < s1 into `out` (RIBBON_POINTS xyz triples). */
export function samplePath(shape: PathShape, ends: PathEnds, s0: number, s1: number, out: Float32Array): void {
  const geometry = PATH_GEOMETRY[shape]
  for (let i = 0; i < RIBBON_POINTS; i++) {
    geometry.point(ends, s0 + ((s1 - s0) * i) / RIBBON_SEGMENTS, scratchA).toArray(out, i * XYZ)
  }
}

/**
 * Samples one piece of a missile's flight arc (render/geo/strikeArc.ts): the great circle `ends` (angle > MIN_PATH_ANGLE),
 * lifted as the share [p0, p1] of a whole flight of `flightAngle` radians, so consecutive pieces meet.
 */
export function sampleFlightPiece(ends: PathEnds, flightAngle: number, p0: number, p1: number, out: Float32Array): void {
  for (let i = 0; i < RIBBON_POINTS; i++) {
    const s = i / RIBBON_SEGMENTS
    slerp(ends, s, radiusAt(ends, s) + flightLift(flightAngle, p0 + (p1 - p0) * s), scratchA).toArray(out, i * XYZ)
  }
}

export function pathLength(shape: PathShape, ends: PathEnds): number {
  return PATH_GEOMETRY[shape].length(ends)
}

/** Some unit tangent of the sphere at unit normal `normal` (east-ish; north-ish at the poles). */
export function tangentAt(normal: Vector3, out: Vector3): Vector3 {
  out.set(normal.z, 0, -normal.x)
  if (out.lengthSq() < MIN_TANGENT_LENGTH_SQ) out.set(0, normal.z, -normal.y)
  return out.normalize()
}

/** Mutable PathEnds backed by owned vectors, for systems that keep a path per slot. */
export class MutablePathEnds implements PathEnds {
  readonly from = new Vector3()
  fromRadius = 0
  readonly to = new Vector3()
  toRadius = 0
  angle = 0

  /** Sets both ends and derives the angle. */
  set(from: Vector3, fromRadius: number, to: Vector3, toRadius: number): this {
    this.from.copy(from)
    this.fromRadius = fromRadius
    this.to.copy(to)
    this.toRadius = toRadius
    this.angle = angleBetween(from, to)
    return this
  }
}
